// «В источнике этих записей нет» — единый способ сказать это словами.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ КОМПОНЕНТ. У пустого экрана в CRM есть ЧЕТЫРЕ разных причины, и человек
// различает их только по нашим словам:
//   1. под фильтры ничего не подошло          — «Ничего не найдено», меняйте условие;
//   2. ядро не ответило                       — QueryState, «Ошибка: …»;
//   3. ядро отказалось обслужить запрос (501) — QueryState, «не может обслужить»;
//   4. ЗАПИСЕЙ НЕТ В САМОМ ИСТОЧНИКЕ          — вот это место.
// Первые три уже разведены (QueryState + `.empty-title` на каждом экране). Четвёртая до
// сих пор нигде не была названа: десять таблиц зеркала пусты (замер `COUNT(*)`,
// 27.07.2026 — `pex_fot`, `pex_plans`, `pex_cash_expenses`, `pbh_rules`, `pbh_s1c_rules`,
// `pbh_user_map`, `pbh_chk_fix`, `p73_autover_photos`, `pex_dept_unit`, `deal_ext`), и
// человек, открывший такой раздел, видит ту же пустоту, что при обрыве синка. Разница
// огромная: в первом случае искать нечего, во втором — данные есть и их не показали.
//
// ПОЧЕМУ ЭТО НЕ ЕЩЁ ОДИН `.empty`, А СВОЙ КОМПОНЕНТ. `.empty` отвечает на вопрос «что я
// ищу» («под эти фильтры актов нет») и зависит от фильтров. Здесь ответ от фильтров НЕ
// зависит вовсе: сколько ни меняй условие, записей не прибавится, потому что их не
// завели в источнике. Сказать это надо один раз и одинаково во всех разделах — иначе
// через месяц в CRM будет пять формулировок одного факта, и ни одной узнаваемой.
//
// ОТКУДА БЕРЁТСЯ ЧИСЛО. Только из ответа ядра (`counts.*`), а не из константы в коде:
// таблица однажды наполнится, и подпись обязана исчезнуть сама. Поэтому `строк` —
// обязательный проп, а не необязательный: подпись без числа нечем опровергнуть.
import type { ReactNode } from 'react'

interface Props {
  /** Чего нет, словами человека: «Фотографий автоверификаций». Не имя таблицы. */
  что: string
  /**
   * Сколько строк в зеркале СЕЙЧАС, по ответу ядра. Ноль — показываем подпись; больше
   * нуля — не показываем ничего, потому что подпись стала неправдой.
   */
  строк: number | null | undefined
  /** Кто должен был их завести: «портал 73», «exec-панель», «бухгалтерия». */
  источник: string
  /** Почему их нет — если это известно. Одно предложение, без точки в конце. */
  почему?: string
  /** Имя таблицы зеркала: тому, кто пойдёт проверять, нужно знать, где смотреть. */
  таблица?: string
  /** Что человеку делать вместо этого раздела. */
  children?: ReactNode
}

/**
 * Возвращает null, когда записи есть: компонент нельзя «включить навсегда». Ровно так же
 * ведёт себя `Закрытое` в Bank.tsx — подпись живёт, пока живо основание для неё.
 */
export function EmptySource({ что, строк, источник, почему, таблица, children }: Props) {
  if (строк === null || строк === undefined || строк > 0) return null
  return (
    <div className="empty">
      <div className="empty-title">{что} нет в источнике</div>
      <div className="empty-hint">
        Это не сбой показа и не обрыв синка: {источник} не завёл ни одной такой записи —
        в зеркале ноль строк{таблица ? ` (${таблица})` : ''}
        {почему ? `. ${почему}` : ''}. Появятся у источника — появятся и здесь, отдельной
        работы для этого не нужно.
        {children ? <> {children}</> : null}
      </div>
    </div>
  )
}
